import axios from "axios";

/**
 * API service for communicating with the backend
 */
class ApiService {
  constructor() {
    this.api = axios.create({
      baseURL:
        process.env.NODE_ENV === "production"
          ? "/api"
          : process.env.REACT_APP_API_URL
          ? `${process.env.REACT_APP_API_URL}/api`
          : "http://localhost:3001/api",
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  /**
   * Get all documents with optional filters
   * @param {Object} params - Query parameters
   * @returns {Promise} API response
   */
  async getDocuments(params = {}) {
    try {
      const response = await this.api.get("/documents", { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching documents:", error);
      throw error;
    }
  }

  /**
   * Get document by ID
   * @param {String} id - Document ID
   * @returns {Promise} API response
   */
  async getDocumentById(id) {
    try {
      const response = await this.api.get(`/documents/${id}`);
      // Return just the document data from the nested structure
      // This ensures consistent data structure with other methods
      if (response.data && response.data.success && response.data.data) {
        return response.data;
      } else {
        console.error(
          `Unexpected response format for document ${id}:`,
          response.data
        );
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error(`Error fetching document ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new document
   * @param {Object} documentData - Document data
   * @returns {Promise} API response
   */
  async createDocument(documentData) {
    try {
      const response = await this.api.post("/documents", documentData);
      if (response.data && response.data.success) {
        return response.data;
      } else {
        console.error(
          "Unexpected response format when creating document:",
          response.data
        );
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Error creating document:", error);
      throw error;
    }
  }

  /**
   * Update an existing document
   * @param {String} id - Document ID
   * @param {Object} documentData - Updated document data
   * @returns {Promise} API response
   */
  async updateDocument(id, documentData) {
    try {
      const response = await this.api.put(`/documents/${id}`, documentData);
      if (response.data && response.data.success) {
        return response.data;
      } else {
        console.error(
          `Unexpected response format when updating document ${id}:`,
          response.data
        );
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error(`Error updating document ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a document
   * @param {String} id - Document ID
   * @returns {Promise} API response
   */
  async deleteDocument(id) {
    try {
      const response = await this.api.delete(`/documents/${id}`);
      if (response.data && response.data.success !== undefined) {
        return response.data;
      } else {
        console.error(
          `Unexpected response format when deleting document ${id}:`,
          response.data
        );
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error(`Error deleting document ${id}:`, error);
      throw error;
    }
  }

  /**
   * Search for documents
   * @param {String} query - Search query
   * @returns {Promise} API response
   */
  async searchDocuments(query) {
    try {
      const response = await this.api.get("/documents/search", {
        params: { q: query },
      });
      return response.data;
    } catch (error) {
      console.error("Error searching documents:", error);
      throw error;
    }
  }
}

// Create singleton instance
const apiService = new ApiService();
export default apiService;
